import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Trash2, Ban, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { media } from "../../lib/community";
import { Byline } from "../../components/community/CommunityKit";
import { AdminSocialPanel } from "../../components/social/AdminSocialPanel";

function PostRow({ p, reload }) {
  const act = (fn, msg) => async () => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className={`bg-white border rounded-2xl p-4 ${p.hidden ? "border-red-200 opacity-70" : "border-[var(--line)]"}`} data-testid={`mod-post-${p.post_id}`}>
      <div className="flex items-start justify-between gap-3">
        {p.author ? <Byline p={p.author} time={p.created_at} /> : <p className="text-sm">Unknown author</p>}
        <div className="flex gap-1 shrink-0">
          <button title={p.hidden ? "Show" : "Hide"} onClick={act(() => api.patch(`/admin/community/posts/${p.post_id}`, { hidden: !p.hidden }), p.hidden ? "Post visible" : "Post hidden")} data-testid={`mod-hide-${p.post_id}`} className="p-2 rounded-full hover:bg-black/5">{p.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
          <button title="Delete" onClick={() => window.confirm("Delete this post permanently?") && act(() => api.delete(`/community/posts/${p.post_id}`), "Post deleted")()} data-testid={`mod-delete-${p.post_id}`} className="p-2 rounded-full hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
          <button title={p.author_banned ? "Unban author" : "Ban author"} onClick={() => window.confirm(p.author_banned ? "Restore this member's community access?" : "Ban this member? Their posts will be hidden and they can't post, comment or message.") && act(() => api.post(`/admin/community/users/${p.author_id}/ban`, { banned: !p.author_banned }), p.author_banned ? "Member restored" : "Member banned")()} data-testid={`mod-ban-${p.post_id}`} className="p-2 rounded-full hover:bg-red-50 text-red-600">{p.author_banned ? <Undo2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}</button>
        </div>
      </div>
      {p.text && <p className="text-sm mt-2 whitespace-pre-wrap">{p.text}</p>}
      {p.images?.length > 0 && <div className="flex gap-2 mt-2">{p.images.map((s) => <img key={s} src={media(s)} alt="" className="w-16 h-16 rounded-lg object-cover" />)}</div>}
      <p className="text-[11px] text-[var(--muted)] mt-2">{p.like_count} likes · {p.comment_count} comments {p.hidden && "· HIDDEN"} {p.author_banned && "· AUTHOR BANNED"}</p>
    </div>
  );
}

export default function AdminCommunityPage() {
  const [status, setStatus] = useState("all");
  const [posts, setPosts] = useState(null);
  const [banned, setBanned] = useState([]);
  const load = useCallback(() => {
    api.get(`/admin/community/posts?status=${status}`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
    api.get("/admin/community/banned").then((r) => setBanned(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);
  async function unban(id) { await api.post(`/admin/community/users/${id}/ban`, { banned: false }); toast.success("Member restored"); load(); }

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-community-page">
      <div>
        <p className="font-script text-[var(--gold)] text-3xl">Community</p>
        <h1 className="font-serif font-light text-4xl">Moderation</h1>
        <p className="text-sm text-[var(--muted)] mt-1">Posts go live instantly. Hide or delete anything off-brand, and ban members who break the rules.</p>
      </div>
      <AdminSocialPanel />
      <div className="flex gap-2">
        {["all", "visible", "hidden"].map((s) => <button key={s} onClick={() => setStatus(s)} data-testid={`mod-filter-${s}`} className={`text-xs uppercase tracking-[0.16em] px-4 py-2 rounded-full border ${status === s ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)]"}`}>{s}</button>)}
      </div>
      {banned.length > 0 && (
        <div className="bg-white border border-[var(--line)] rounded-2xl p-4" data-testid="mod-banned-list">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mb-2">Banned members</p>
          {banned.map((b) => <div key={b.user_id} className="flex items-center justify-between py-1.5 text-sm"><span>{b.display_name}</span><button onClick={() => unban(b.user_id)} className="text-xs text-[var(--gold)]" data-testid={`mod-unban-${b.user_id}`}>Restore</button></div>)}
        </div>
      )}
      <div className="space-y-3">
        {posts?.length === 0 && <p className="text-sm text-[var(--muted)] text-center py-10" data-testid="mod-empty">No posts in this view.</p>}
        {posts?.map((p) => <PostRow key={p.post_id} p={p} reload={load} />)}
      </div>
    </div>
  );
}
